import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";
import Pagination from "../../shared/common/pagination";
import Loader from "../../shared/loader/loader";
import DeleteModal from "../../shared/common/deleteModal";
import { IGrade, IGradeInput } from "../../../model/interface/grade";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Constant from "../../../utils/constant";
import {
  AddIcon,
  FilterIcon,
  EditIcon,
  DeleteIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CSVIcon,
  ApproveIcon,
  Disapprove,
} from "../../../assets/icons/index";
import ExportToCSV from "../../shared/common/exportToCsv";
import { toast } from "react-toastify";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";

const Grade: React.FC = () => {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const initialState = {
    gradeId: 0,
    name: "",
    abbreviation: "",
  };
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [newGrade, setNewGrade] = useState<IGrade[]>([]);
  const [deleteGrade, setDeleteGrade] = useState<any>(null);
  const [filterInput, setFilterInput] = useState<IGradeInput>(initialState);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);
  const [sortCol, setSortCol] = useState<string>("Name");
  const childRef: any = useRef(undefined);

  const deleteGradeHandler = (e, id) => {
    e.preventDefault();
    setShowDeleteModal(true);
    const deleteGrade = newGrade.find((grade) => grade.gradeId === id);
    setDeleteGrade(deleteGrade);
  };

  const deleteRecordHandler = async () => {
    const service = await import("../../../api/grade.service");
    service.deleteGradeSection(deleteGrade.gradeId).then((res) => {
      if (res?.data?.status === "Error") {
        toast.warning(res?.data?.message);
        setTimeout(() => {
          setShowDeleteModal(false);
          filterGrades(false, false);
        }, 1500);
      } else {
        toast.success("Deleted successfully");
        setTimeout(() => {
          setShowDeleteModal(false);
          filterGrades(false, false);
        }, 1500);
      }
    });
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const filterInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    let value: any = target.value;
    const name = target.name;

    if (name === "gradeId") {
      value = Number(target.value);
    }

    setFilterInput({
      ...filterInput,
      [name]: value,
    });

    if (e.target.value === "") {
      filterGrades(
        {
          ...initialState,
          pageNumber: defaultPageNumber,
          pageSize: defaultPageSize,
        },
        false
      );
    }
  };

  const filterHandler = (e) => {
    e.preventDefault();
    filterGrades(false, true);
  };

  const filterGrades = async (resetObj?, onFilter?) => {
    if (onFilter) setIsFilter(true);

    setLoading(true);
    const grade = {
      gradeId: filterInput.gradeId,
      name: filterInput.name,
      abbreviation: filterInput.abbreviation,
      orderByColumn: sortCol,
      sortDir: sortByDesc ? "DESC" : "ASC",
      pageNumber: onFilter ? 1 : pageNumber,
      pageSize: onFilter ? 9999 : pageSize,
    };

    const service = await import("../../../api/grade.service");
    const response = service.getGrade(resetObj ? resetObj : grade);
    const data = (await response).data;
    setNewGrade(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
    if (onFilter) {
      paginationRef?.current?.onReset();
      pageSizeRef?.current?.onReset();

      setIsFilter(false);
    }
    setLoading(false);
  };

  const resetClickHandler = () => {
    setFilterInput({
      ...initialState,
    });

    filterGrades(
      {
        ...initialState,
        pageNumber: defaultPageNumber,
        pageSize: defaultPageSize,
      },
      false
    );
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
  };

  const handleRedirect = (grade?: IGrade) => {
    navigate(
      grade
        ? Constant.EDITGRADE.replace(
            ":id",
            grade.gradeId ? grade.gradeId.toString() : ""
          )
        : Constant.ADDGRADE,
      { state: { objectToEdit: grade } }
    );
  };

  const handleSortBy = (column: string) => {
    setSortByDesc(!sortByDesc);
    setSortCol(column);
  };

  useEffect(() => {
    if (isLoaded && !isFilter) {
      filterGrades(false, false);
    }
  }, [sortByDesc]);

  useEffect(() => {
    if (isLoaded && !isFilter) {
      filterGrades(false, false);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1 && !isFilter) {
      filterGrades(false, false);
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const exportFn = async () => {
    setLoading(true);
    const grade = {
      gradeId: filterInput.gradeId,
      name: filterInput.name,
      abbreviation: filterInput.abbreviation,
      orderByColumn: sortCol,
      sortDir: sortByDesc ? "DESC" : "ASC",
      pageNumber: 1,
      pageSize: 9999,
    };

    const service = await import("../../../api/grade.service");
    const response = service.getGrade(grade);
    const data = (await response).data;
    setNewGrade(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
    setLoading(false);

    if (data && data.data && data.data.length > 0) {
      childRef?.current?.onDownload(data.data, "grades.csv");
    } else {
      toast.warning("No records to download");
    }
  };

  /** Code to implement input type number to support all browsers */
  useEffect(() => {
    const input = document.getElementById("gradeId");
    if (input) {
      input.addEventListener("keypress", (e: any) => {
        const keyValue = e.key;
        if (!e.target.value && e.key == 0) {
          e.preventDefault();
        } else {
          if (!/[0-9.]/.test(keyValue)) {
            e.preventDefault();
          }
        }
      });
    }
  }, []);

  return (
    <AllowedTo perform={Permissions.grade_view} no={() => <AccessDenied />}>
      {loading && <Loader />}

      {showDeleteModal && (
        <DeleteModal
          closeDeleteModal={closeDeleteModal}
          deleteRecord={deleteRecordHandler}
        />
      )}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Grade</h3>
              <AllowedTo perform={Permissions.grade_create}>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                  onClick={() => handleRedirect()}
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Grade
                </button>
              </AllowedTo>
            </div>
            <form action="#" onSubmit={filterHandler}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-3 sm:col-span-3 ">
                    <label className="text-sm text-gray-700">Grade Id</label>
                    <div className="contentArea">
                      <input
                        id="gradeId"
                        name="gradeId"
                        type="text"
                        autoComplete="off"
                        className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput.gradeId || ""}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>

                  <div className="col-span-3 sm:col-span-3 ">
                    <label className="text-sm text-gray-700">Name</label>
                    <div className="contentArea">
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="off"
                        className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput.name}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">
                      Abbreviation
                    </label>
                    <div className="contentArea">
                      <input
                        id="abbreviation"
                        name="abbreviation"
                        type="text"
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput.abbreviation}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-start">
                  <button
                    type="submit"
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <FilterIcon className="w-3 h-3"></FilterIcon>
                    <span>Filter</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => resetClickHandler()}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                  >
                    <ResetIcon className="w-3 h-3"></ResetIcon>
                    <span>Reset Filter</span>
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => exportFn()}
                  className="mt-4 py-1.5 px-4 inline-flex ml-auto items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <CSVIcon className="w-3 h-3 dark:text-white stroke-current"></CSVIcon>
                  <span>Export</span>
                </button>
                <ExportToCSV ref={childRef} />
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="inline-flex items-center gap-2 px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Id
                                <span className="cursor-pointer">
                                  {!sortByDesc && sortCol === "GradeId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("GradeId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("GradeId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Name
                                <span className="cursor-pointer">
                                  {!sortByDesc && sortCol === "Name" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("Name")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("Name")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Abbreviation
                                <span className="cursor-pointer">
                                  {!sortByDesc && sortCol === "Abbreviation" ? (
                                    <CaretUpSolidIcon
                                      onClick={() =>
                                        handleSortBy("Abbreviation")
                                      }
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() =>
                                        handleSortBy("Abbreviation")
                                      }
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">IsActive</span>
                            </th>
                            <th
                              scope="col"
                              className="w-12 px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {newGrade.map((grade, index) => (
                            <tr
                              key={grade.gradeId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td
                                className="px-3 py-2 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                onClick={(e) => handleRedirect(grade)}
                              >
                                <Link
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  to={Constant.EDITGRADE.replace(
                                    ":id",
                                    grade.gradeId
                                      ? grade.gradeId.toString()
                                      : ""
                                  )}
                                >
                                  {grade.gradeId}
                                </Link>
                              </td>
                              <td className="px-3 py-2 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline">
                                <Link
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  to={Constant.EDITGRADE.replace(
                                    ":id",
                                    grade.gradeId
                                      ? grade.gradeId.toString()
                                      : ""
                                  )}
                                >
                                  {grade.name}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                <p>{grade.abbreviation}</p>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                <div className="flex justify-end items-center space-x-2">
                                  {grade.isActive ? (
                                    <ApproveIcon title="Active" />
                                  ) : (
                                    <Disapprove />
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-center font-normal text-gray-700">
                                <div className="flex gap-3 justify-center">
                                  <AllowedTo perform={Permissions.grade_modify}>
                                    <EditIcon
                                      onClick={(e) => handleRedirect(grade)}
                                      className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                  {grade.isActive && (
                                    <AllowedTo
                                      perform={Permissions.grade_delete}
                                    >
                                      <DeleteIcon
                                        onClick={(e) =>
                                          deleteGradeHandler(e, grade.gradeId)
                                        }
                                        className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                      />
                                    </AllowedTo>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex justify-between items-center ${
                    newGrade.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={totalPages}
                    setPageNumber={setPageNumber}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {pageNumber} to{" "}
                      {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                      {totalRecords} results
                    </span>

                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>

                {newGrade.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default Grade;
